/**
 * Suppression d'une organisation — dialogue de confirmation (spec 2026-09-27 §1.4).
 *
 * Charge l'inventaire (`GET /organizations/:id/deletion-preview`) pour montrer ce qui disparaît
 * AVANT de proposer la confirmation, et n'active le bouton de suppression que si le texte tapé
 * correspond exactement au nom de l'organisation — double protection : le serveur revérifie la
 * même égalité et refuse en 400 `CONFIRMATION_MISMATCH` s'il ne fait pas confiance au client
 * (§1.3 de la spec), mais l'écran ne doit jamais laisser croire qu'un clic suffit.
 *
 * Erreur → message affiché ICI, pas de redirection (§1.4). Succès → à la charge de l'appelant
 * (`onDeleted`), pas de ce composant : lui seul sait qu'il faut aussi rafraîchir la liste des
 * organisations de l'`AppShell` et rediriger vers `/profil`.
 *
 * Un 401 `REAUTH_REQUIRED` (§ revue de sécurité 2026-09-27, spec §1.2) n'est pas une erreur
 * comme les autres : la session est valide mais trop ancienne pour ce geste irréversible.
 * `api.js` ne déclenche PAS la déconnexion globale pour ce code — sans quoi l'utilisateur
 * atterrirait sur l'écran de connexion sans explication — donc c'est ici, et seulement ici,
 * qu'un bouton « Se reconnecter » propose de rafraîchir la session avant de retenter.
 *
 * Lot 2 (spec CIAM) : en mode porteur (`ciamConfigured`), une simple session cookie n'existe
 * plus — un renouvellement silencieux du jeton ne rajeunit pas `auth_time` (§ `middleware.js`
 * serveur, `requireRecentReauth`), seule une interaction le fait. `reconnect()` relance donc
 * `auth.login(returnTo, { prompt: 'login' })`, qui force cette interaction. Pendant la
 * coexistence (D7) sans configuration CIAM côté build, le repli legacy est conservé :
 * fournisseur pris sur la session courante (même source que `ProfilePage`) ; `local` (bypass
 * dev) ou un fournisseur inconnu n'ont pas de flux OAuth à relancer → repli sur `/connexion`.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { reconnectForReauth } from '../lib/reauth.js';
import { useAsync } from '../hooks/useAsync.js';
import { useSession } from '../contexts/SessionContext.jsx';
import { Modal } from '../components/Modal.jsx';
import { TypeToConfirmField, typeToConfirmMatches } from '../components/TypeToConfirmField.jsx';
import { BTN_GHOST_SM, BTN_PRIMARY_SM } from '../ui/theme.js';

export function DeleteOrganizationDialog({ organizationId, organizationName, onClose, onDeleted }) {
  const { t } = useTranslation();
  const { user } = useSession();
  const [confirmName, setConfirmName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [reauthRequired, setReauthRequired] = useState(false);

  const {
    data: previewData,
    loading: previewLoading,
    error: previewError,
  } = useAsync(
    (signal) => api.get(`/organizations/${organizationId}/deletion-preview`, { signal }),
    [organizationId],
  );
  const preview = previewData?.data;

  const matches = typeToConfirmMatches(confirmName, organizationName);

  const confirmDelete = async () => {
    if (!matches) return;
    setBusy(true);
    setError(null);
    setReauthRequired(false);
    try {
      await api.delete(`/organizations/${organizationId}`, { body: { confirmName } });
      onDeleted();
    } catch (deleteError) {
      if (deleteError.status === 401 && deleteError.details?.code === 'REAUTH_REQUIRED') {
        setReauthRequired(true);
      } else {
        setError(deleteError.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const reconnect = () => reconnectForReauth(`/organisation/${organizationId}`, user);

  return (
    <Modal
      title={t('organization.danger.dialogTitle', { name: organizationName })}
      onClose={onClose}
    >
      <div className="flex flex-col gap-4">
        {previewLoading ? (
          <p className="text-sm text-ink-soft">{t('organization.danger.loading')}</p>
        ) : previewError ? (
          <p role="alert" className="text-sm text-ink">
            {t('organization.danger.loadError', { message: previewError.message })}
          </p>
        ) : (
          <>
            <p className="text-sm text-ink">{t('organization.danger.intro')}</p>

            <ul className="flex flex-col gap-1 text-sm text-ink-soft">
              <li>{t('organization.danger.inventory.teams', { count: preview.teams })}</li>
              <li>{t('organization.danger.inventory.projects', { count: preview.projects })}</li>
              <li>{t('organization.danger.inventory.tasks', { count: preview.tasks })}</li>
              <li>{t('organization.danger.inventory.members', { count: preview.members })}</li>
              <li>
                {t('organization.danger.inventory.pendingInvitations', {
                  count: preview.pendingInvitations,
                })}
              </li>
            </ul>

            {preview.hasLicenceKey && (
              <p
                role="alert"
                className="border-2 border-accent-trait bg-accent-wash px-3 py-2 text-sm text-accent-ink"
              >
                {t('organization.danger.licenceWarning')}
              </p>
            )}

            <TypeToConfirmField
              id="delete-organization-confirm-name"
              label={t('organization.danger.confirmLabel', { name: organizationName })}
              value={confirmName}
              onChange={setConfirmName}
              disabled={busy}
            />

            {reauthRequired ? (
              <div className="flex flex-col items-start gap-2 border-2 border-accent-trait bg-accent-wash px-3 py-2">
                <p role="alert" className="text-sm text-accent-ink">
                  {t('organization.danger.reauthRequired')}
                </p>
                <button type="button" className={BTN_GHOST_SM} onClick={reconnect}>
                  {t('organization.danger.reconnect')}
                </button>
              </div>
            ) : (
              error && (
                <p role="alert" className="text-sm text-ink">
                  {t('organization.danger.deleteFailed', { message: error })}
                </p>
              )
            )}

            <div className="flex justify-end gap-2">
              <button type="button" className={BTN_GHOST_SM} disabled={busy} onClick={onClose}>
                {t('organization.danger.cancel')}
              </button>
              <button
                type="button"
                className={BTN_PRIMARY_SM}
                disabled={busy || !matches}
                onClick={confirmDelete}
              >
                {t('organization.danger.confirmButton')}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
